/**
 * API 配置工具
 * 统一管理 API 基础地址，支持开发和生产环境
 * 
 * 配置优先级：
 * 1. REACT_APP_USE_SAME_DOMAIN=true（同域部署，使用相对路径）
 * 2. REACT_APP_API_BASE_URL 环境变量（指定后端地址）
 * 3. 开发环境：自动使用 localhost:8000
 * 4. 生产环境：如果未配置环境变量，使用相对路径（假设同域部署）
 */

// 获取 API 基础地址
const getApiBaseUrl = (): string => {
  // 优先级1: 检查是否使用同域部署（最高优先级）
  // 支持多种格式：'true', 'True', 'TRUE', '1'
  const useSameDomain = process.env.REACT_APP_USE_SAME_DOMAIN;
  if (useSameDomain && (useSameDomain.toLowerCase() === 'true' || useSameDomain === '1')) {
    return '';
  }
  
  // 优先级2: 环境变量指定后端地址
  if (process.env.REACT_APP_API_BASE_URL) {
    const url = process.env.REACT_APP_API_BASE_URL.trim();
    // 如果配置为空字符串，表示使用相对路径（同域部署）
    if (url === '') {
      return '';
    }
    return url;
  }
  
  // 优先级3: 开发环境使用 localhost
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:8000';
  }
  
  // 优先级4: 生产环境
  // 如果未配置任何环境变量，默认使用相对路径（假设前后端同域部署）
  // 这样可以避免生产环境回退到 localhost
  if (process.env.NODE_ENV === 'production') {
    console.warn(
      '⚠️ 生产环境未配置 API 地址环境变量。\n' +
      '当前使用相对路径（假设前后端同域部署）。\n' +
      '如果后端在不同域名，请在部署平台的项目设置中添加环境变量：\n' +
      '  REACT_APP_API_BASE_URL=https://your-backend-api.com\n' +
      '或者如果前后端在同一域名：\n' +
      '  REACT_APP_USE_SAME_DOMAIN=true'
    );
    // 生产环境默认使用相对路径，而不是 localhost
    return '';
  }
  
  // 降级到 localhost（仅用于开发环境）
  return 'http://localhost:8000';
};

export const API_BASE_URL = getApiBaseUrl();

/**
 * 构建完整的 API URL
 * @param endpoint API 端点路径（例如：'/api/analyze/stream'）
 * @returns 完整的 API URL
 */
export const buildApiUrl = (endpoint: string): string => {
  // 确保 endpoint 以 / 开头
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  
  // 如果 API_BASE_URL 为空，使用相对路径（同域部署）
  if (!API_BASE_URL || API_BASE_URL === '') {
    return normalizedEndpoint;
  }
  
  // 移除 API_BASE_URL 末尾的斜杠（如果有）
  const baseUrl = API_BASE_URL.endsWith('/') 
    ? API_BASE_URL.slice(0, -1) 
    : API_BASE_URL;
  
  return `${baseUrl}${normalizedEndpoint}`;
};

// 导出常用的 API 端点
export const API_ENDPOINTS = {
  ANALYZE_STREAM: '/api/analyze/stream',
  TRENDING_ASSETS: '/api/trending/assets',
  TRENDING_VARIABLES: '/api/trending/variables',
  ADMIN_CONFIG: '/api/admin/config',
  ADMIN_PROMPT: '/api/admin/prompt',
} as const;


